import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { CookieService } from 'ngx-cookie-service';
import { Observable } from 'rxjs';
import { global } from '../global';

export type SocialNetwork = 'INSTAGRAM' | 'FACEBOOK' | 'LINKEDIN' | 'TWITTER' | 'YOUTUBE' | 'TIKTOK';
export type PostStatus = 'DRAFT' | 'SCHEDULED' | 'PUBLISHING' | 'PUBLISHED' | 'FAILED' | 'CANCELLED';
export type TargetStatus = 'PENDING' | 'PUBLISHING' | 'PUBLISHED' | 'FAILED';
export type SocialMediaType = 'NONE' | 'IMAGE' | 'VIDEO';

export interface SocialAccount {
  accountId: number;
  network: SocialNetwork;
  externalUsername: string;
  active: boolean;
  tokenValid: boolean;
  connectedAt: string;
  tokenExpiresAt: string;
}

export interface SocialPostTarget {
  targetId: number;
  network: SocialNetwork;
  status: TargetStatus;
  externalPostId?: string;
  externalUrl?: string;
  errorMessage?: string;
  attempts: number;
  publishedAt?: string;
}
export interface SocialCommentDto {
  externalId: string;
  parentCommentId?: string | null;
  postId?: number | null;
  network: string;

  username?: string | null;
  message: string;

  createdAt?: string | null;

  replies: SocialCommentDto[];
}

export interface SocialPost {
  postId: number;
  content: string;
  mediaPath?: string;
  mediaType: SocialMediaType;
  networkMetadata?: { [network: string]: { [key: string]: any } };
  status: PostStatus;
  scheduledAt: string;
  createdAt: string;
  publishedAt?: string;
    likes?: number;
  comments?: number;
  targets: SocialPostTarget[];
  commentList?: SocialCommentDto[];
}

export interface SocialPostRequest {
  content: string;
  mediaPath?: string;
  mediaType: SocialMediaType;
  targetNetworks: SocialNetwork[];
  networkMetadata?: { [network: string]: { [key: string]: any } };
  scheduledAt?: string;
  publishNow?: boolean;
}

export interface MediaUploadResponse {
  mediaPath: string;
  mediaType: SocialMediaType;
  sizeBytes: number;
  originalFilename: string;
}

export interface OAuthStartResponse {
  authorizationUrl: string;
  state: string;
  codeVerifier?: string;
}

export interface GenerateTextRequest {
  prompt: string;
  tone?: string;
  maxLength?: number;
  targetNetworks?: string[];
}

export interface GenerateTextResponse {
  generatedText: string;
}

export interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

@Injectable({ providedIn: 'root' })
export class SocialService {

  private baseUrl = global.url + 'social';

  constructor(private http: HttpClient, private cookieService: CookieService) {}

  private authHeaders(): HttpHeaders {
    const token = this.cookieService.get('auth_token');
    return new HttpHeaders({ 'licencia': token });
  }

  // ====== CUENTAS / OAUTH ======

  startOAuth(network: SocialNetwork, redirectUri: string): Observable<OAuthStartResponse> {
    const params = new HttpParams().set('redirectUri', redirectUri);
    return this.http.get<OAuthStartResponse>(
      `${this.baseUrl}/accounts/oauth/${network}/start`,
      { headers: this.authHeaders(), params }
    );
  }

  completeOAuth(network: SocialNetwork, code: string, state: string, redirectUri: string, pageId?: string,  codeVerifier?: string): Observable<SocialAccount> {
    return this.http.post<SocialAccount>(
      `${this.baseUrl}/accounts/oauth/${network}/callback`,
      { code, state, redirectUri,codeVerifier, ...(pageId ? { pageId } : {}) },
      { headers: this.authHeaders() }
    );
  }

  getAccounts(): Observable<SocialAccount[]> {
    return this.http.get<SocialAccount[]>(
      `${this.baseUrl}/accounts`,
      { headers: this.authHeaders() }
    );
  }

  disconnectAccount(accountId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.baseUrl}/accounts/${accountId}`,
      { headers: this.authHeaders() }
    );
  }

  /** Activa Facebook usando el Page Token estático configurado en el servidor (sin OAuth). */
  setupStaticFacebook(): Observable<SocialAccount> {
    return this.http.post<SocialAccount>(
      `${this.baseUrl}/accounts/FACEBOOK/static`,
      {},
      { headers: this.authHeaders() }
    );
  }

  // ====== POSTS ======

  createPost(request: SocialPostRequest): Observable<SocialPost> {
    return this.http.post<SocialPost>(
      `${this.baseUrl}/posts`,
      request,
      { headers: this.authHeaders() }
    );
  }

  updatePost(postId: number, request: SocialPostRequest): Observable<SocialPost> {
    return this.http.put<SocialPost>(
      `${this.baseUrl}/posts/${postId}`,
      request,
      { headers: this.authHeaders() }
    );
  }

  getPost(postId: number): Observable<SocialPost> {
    return this.http.get<SocialPost>(
      `${this.baseUrl}/posts/${postId}`,
      { headers: this.authHeaders() }
    );
  }

  listPosts(status: PostStatus | null, page: number = 0, size: number = 20): Observable<PageResponse<SocialPost>> {
    let params = new HttpParams().set('page', page).set('size', size);
    if (status) params = params.set('status', status);
    return this.http.get<PageResponse<SocialPost>>(
      `${this.baseUrl}/posts`,
      { headers: this.authHeaders(), params }
    );
  }

  cancelPost(postId: number): Observable<void> {
    return this.http.post<void>(
      `${this.baseUrl}/posts/${postId}/cancel`,
      {},
      { headers: this.authHeaders() }
    );
  }

  deletePost(postId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.baseUrl}/posts/${postId}`,
      { headers: this.authHeaders() }
    );
  }

  retryTarget(targetId: number): Observable<void> {
    return this.http.post<void>(
      `${this.baseUrl}/posts/targets/${targetId}/retry`,
      {},
      { headers: this.authHeaders() }
    );
  }

  generateText(request: GenerateTextRequest): Observable<GenerateTextResponse> {
    return this.http.post<GenerateTextResponse>(
      `${this.baseUrl}/posts/generate-text`,
      request,
      { headers: this.authHeaders() }
    );
  }

  // ====== MEDIA ======

  uploadMedia(file: File): Observable<MediaUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<MediaUploadResponse>(
      `${this.baseUrl}/media/upload`,
      formData,
      { headers: this.authHeaders() }
    );
  }
}

